/**
 * Detect the gateway "pairing required" handshake rejection.
 *
 * When a stored pairing token has been revoked (or was never issued), the
 * gateway answers `connect` with an error whose message contains
 * "pairing required" / "pair this device" / similar. We key off the
 * substring rather than exact-match because the gateway wording is not
 * guaranteed stable across versions.
 */
export function isPairingRequiredError(
  errorMessage: string | null | undefined,
): boolean {
  if (errorMessage == null) return false;
  return /\bpair(ing)?\b/iu.test(errorMessage);
}

/**
 * Detect a gateway token mismatch — the stored token no longer matches the
 * gateway (e.g. the gateway was rotated or restarted). The user must paste
 * the new token from the OpenClaw dashboard.
 */
export function isTokenMismatchError(
  errorMessage: string | null | undefined,
): boolean {
  if (errorMessage == null) return false;
  return /token.mismatch/iu.test(errorMessage);
}

/**
 * What to tell the user about cross-device sync, or null when nothing is
 * wrong. Read off the sync client's status (see `AgentState.syncStatus`).
 *
 * SETTINGS, not chat history. This used to say "Chat history isn't syncing",
 * which was true when a chat's transcript and the chat index were replicated
 * as `/sync/v1` documents authored by whichever browser tab was watching.
 * They are not any more: the agent holds every conversation and the terminal
 * reads it back, so the only document this client still PUTs is the
 * preferences doc (`ui-state`). Every failure that can now reach here is a
 * settings write — and telling a user their chat history is at risk when
 * their theme failed to replicate is worse than saying nothing, because the
 * one thing this banner exists to warn about is the thing that is now safe.
 *
 * Nothing is said about `droppedSessionIds` any more. It was the durable
 * record of a transcript the gateway refused for good — a 413 on a document
 * over the 8 MiB cap — which is unreachable now that no transcript is
 * uploaded.
 *
 * One session op DOES remain in the queue, and it is the reason for the
 * `delete_` guard below rather than a comment: `deleteSession` queues a
 * DELETE of the chat's archive document, and a refusal sets
 * `lastError = delete_<status>` and keeps the op pending, so the error
 * persists until the next successful request of any kind. Falling through to
 * the settings copy told a user their settings were not syncing because a
 * chat DELETION was refused — a statement about a failure that did not
 * happen. It gets no banner at all: the delete that matters already happened
 * locally and on the agent, and what is left behind is an orphaned document
 * for a chat gone from every roster. Nothing a user can see or act on.
 *
 * `lastError` is cleared by the next successful request of any kind, so a
 * transient blip shows for one retry interval at most; a real outage stays.
 */
export function describeSyncFailure(
  status: {
    readonly lastError: string | null;
  } | null,
): string | null {
  if (status == null) return null;
  const err = status.lastError;
  if (err == null) return null;
  // Not a settings failure, and not user-visible. See the note above.
  if (err.startsWith('delete_')) return null;
  // Matched on the STATUS, not the whole code.
  //
  // A write that loses a cross-device race is retried after resolving the
  // conflict, and that second attempt records itself differently:
  // `put_retry_401`, `put_retry_507`. Comparing whole strings meant every
  // one of those fell through to "Retrying" — so a permission failure on the
  // retry read as a transient blip, and the user waited for a recovery that
  // was never coming.
  const httpStatus = err.match(/_(\d{3})$/u)?.[1];
  if (httpStatus === '507') {
    return "Your settings aren't syncing: the agent's storage is full.";
  }
  if (err === 'no_auth' || httpStatus === '401' || httpStatus === '403') {
    return "Your settings aren't syncing to your other devices (not authorized).";
  }
  return "Your settings aren't syncing to your other devices right now. Retrying.";
}
